/**
 * Waitlist capture:
 * - Optional Formspree: REACT_APP_WAITLIST_ENDPOINT
 * - Optional Vercel API for daily reports: REACT_APP_WAITLIST_INGEST_URL → /api/waitlist-ingest
 * - Always backs up to localStorage (export via window.__ORIVON_WAITLIST_EXPORT).
 *
 * Formspree: https://formspree.io → REACT_APP_WAITLIST_ENDPOINT (e.g. https://formspree.io/f/xxxxxxxx)
 */

const STORAGE_KEY = 'orivon_waitlist_entries';

function backupLocal(payload) {
  try {
    const prev = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    prev.push(payload);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prev.slice(-1000)));
  } catch (_) {
    /* ignore quota / private mode */
  }
}

/**
 * @param {{
 *   email: string,
 *   firstName?: string,
 *   lastName?: string,
 *   birthday?: string,
 *   chapterInterests?: string[],
 *   source?: string,
 *   referralCode?: string,
 *   note?: string
 * }} data
 * @returns {Promise<{ ok: boolean, mode: 'remote' | 'local-only', error?: string }>}
 */
export async function submitWaitlist(data) {
  const email = String(data.email || '').trim();
  const firstName = String(data.firstName || '').trim();
  const lastName = String(data.lastName || '').trim();
  const birthday = String(data.birthday || '').trim();
  const chapterInterests = Array.isArray(data.chapterInterests)
    ? data.chapterInterests.map((v) => String(v || '').trim()).filter(Boolean)
    : [];
  const source = String(data.source || '').trim() || 'unknown';
  const referralCode = String(data.referralCode || '').trim();
  const note = String(data.note || '').trim();

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return { ok: false, mode: 'local-only', error: 'Please enter a valid email.' };
  }

  const payload = {
    email,
    firstName: firstName || undefined,
    lastName: lastName || undefined,
    birthday: birthday || undefined,
    chapterInterests: chapterInterests.length ? chapterInterests : undefined,
    source,
    referralCode: referralCode || undefined,
    note: note || undefined,
    submittedAt: new Date().toISOString(),
    _subject: `[OrivonAI waitlist] ${source} — ${email}`,
  };

  backupLocal(payload);

  const endpoint = process.env.REACT_APP_WAITLIST_ENDPOINT;
  // Default to same-origin API in production so waitlist works without extra frontend env config.
  // An explicit REACT_APP_WAITLIST_INGEST_URL still overrides this.
  const ingestUrl = process.env.REACT_APP_WAITLIST_INGEST_URL || '/api/waitlist-ingest';

  const ingestBody = JSON.stringify({
    email: payload.email,
    firstName: payload.firstName,
    lastName: payload.lastName,
    birthday: payload.birthday,
    chapterInterests: payload.chapterInterests,
    source: payload.source,
    referralCode: payload.referralCode,
    note: payload.note,
    submittedAt: payload.submittedAt,
  });

  const runIngest = () =>
    ingestUrl
      ? fetch(ingestUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: ingestBody,
        }).catch(() => null)
      : Promise.resolve(null);

  if (!endpoint) {
    const ingestRes = await runIngest();
    if (!ingestRes) {
      return { ok: false, mode: 'local-only', error: 'Could not reach the server. Check your connection and try again.' };
    }
    const data = await ingestRes.json().catch(() => ({}));
    if (!ingestRes.ok) {
      return {
        ok: false,
        mode: 'local-only',
        error: data.error || data.hubspotMessage || `Server error (${ingestRes.status})`,
      };
    }
    const remote = !!(data.hubspotSynced || data.stored);
    return { ok: true, mode: remote ? 'remote' : 'local-only' };
  }

  try {
    const [ingestRes, formRes] = await Promise.all([
      runIngest(),
      fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          ...payload,
          _replyto: email,
        }),
      }),
    ]);

    if (formRes.ok) {
      return { ok: true, mode: 'remote' };
    }

    if (ingestRes && ingestRes.ok) {
      return { ok: true, mode: 'remote' };
    }

    const text = await formRes.text();
    return {
      ok: false,
      mode: 'local-only',
      error: text || `Request failed (${formRes.status})`,
    };
  } catch (e) {
    return {
      ok: false,
      mode: 'local-only',
      error: e?.message || 'Network error',
    };
  }
}

/** Export backup JSON from localStorage (call from devtools or a future admin UI). */
export function exportWaitlistBackup() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

/** In browser devtools: `copy(JSON.stringify(window.__ORIVON_WAITLIST_EXPORT(), null, 2))` then paste into a file. */
if (typeof window !== 'undefined') {
  window.__ORIVON_WAITLIST_EXPORT = exportWaitlistBackup;
}
